"use client";

import React from "react";
/* ⚠ `Element` is aliased: the parser exports a type called `Element`, which
   would shadow the DOM's global. The house idiom (legal-modal) aliases it, so
   the two read the same. */
import parse, {
  domToReact,
  type DOMNode,
  type Element as ParsedElement,
  type HTMLReactParserOptions
} from "html-react-parser-v6";
import { Text, hasMarkup, needsBlockHost } from "../../atoms/text/text";
import { LINK_POSITION_BODY, fireLinkClick } from "../../services/analytics";
import styles from "./hp-tab-carousel.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * HpTabCarouselSubheading — the section subhead, with its emphasised phrases
 * wired to the tab tray.
 *
 * The subhead is authored CMS markup — the same string the Text atom would
 * render. A <strong> run whose text names a tab (matched to a `tab_label` by
 * `matchKey`) becomes a real <button> that switches to that tab, driving the
 * SAME `activeValue`/`onSelect` the pill tray drives. A <strong> that matches no
 * tab stays plain emphasis. No new CMS field: the author just bolds the tab name.
 *
 * WHY THIS IS A CLIENT COMPONENT: the switch buttons carry an onClick, which a
 * Server Component cannot hold — so the subhead's HTML crosses the boundary as a
 * STRING and is parsed here, rather than arriving already rendered like the
 * headline beside it. That bundle cost (this leaf plus html-react-parser-v6,
 * already in the client bundle for legal-modal) is the price of the feature,
 * recorded in design-conflicts/hp-tab-carousel.mdx.
 *
 * ⚠ These are a SECONDARY in-text switch, NOT an APG tab: the tray is the
 * radiogroup. So the active phrase reflects selection with `aria-pressed`, never
 * `role="tab"`/`aria-selected` — there is no tablist for them to belong to.
 */
export interface HpTabCarouselSubheadingProps {
  /** The subhead's CMS markup. A bold run naming a tab becomes a switch button;
   *  with no match it renders exactly as the Text atom would. */
  content: string;
  /** The tabs to match against and switch between, by `value` and `label`. Empty
   *  — a single-panel section, or the tray turned off — leaves every phrase plain
   *  emphasis, since there is nothing to switch to. */
  items: { value: string; label: string }[];
  /** The selected tab's value. The phrase matching it is `aria-pressed`. */
  activeValue: string;
  /** Switches the active tab. Wired to the tray's own setter, so the two controls
   *  move one selection. */
  onSelect: (value: string) => void;
  /** Where the section sits on the page, for the `link_click` a phrase reports.
   *  Defaults to the body position — matches the tab tray's own reporting. */
  linkPosition?: string;
}

/** A phrase or a tab label reduced to its match key: trimmed, a single trailing
 *  comma or period dropped, then lower-cased — so "MyFree DIRECTV." in the
 *  sentence matches the "MyFree DIRECTV" tab and "genre packs" matches
 *  "Genre Packs". */
function matchKey(value: string): string {
  return value.trim().replace(/[.,]$/, "").trim().toLowerCase();
}

function isElement(node: DOMNode): node is ParsedElement {
  return (node as { type?: string }).type === "tag";
}

/** A parsed node's text, joined across its children — the phrase as authored, for
 *  matching. A matched run is plain text, so this reads it the way the DOM does. */
function textOf(node: DOMNode): string {
  if (isElement(node)) {
    return (node.children as DOMNode[]).map(textOf).join("");
  }
  return "data" in node && typeof node.data === "string" ? node.data : "";
}

function HpTabCarouselSubheadingImpl({
  content,
  items,
  activeValue,
  onSelect,
  linkPosition
}: HpTabCarouselSubheadingProps) {
  /* Label → value, first spelling wins. Built per render from `items`, which is a
     handful of tabs — not expensive work, so no memo to earn. */
  const valueByKey = new Map<string, string>();
  for (const item of items) {
    const key = matchKey(item.label);
    if (key !== "" && !valueByKey.has(key)) {
      valueByKey.set(key, item.value);
    }
  }

  /* A phrase is a SECONDARY switch for the same tab the tray switches, so it
     reports its own `link_click` just as the tray does — selecting through
     `onSelect` moves the tray's state programmatically, which the tray does NOT
     report (it fires only for its own native activation), so without this the
     subhead switch would be silent. The tray reports the tab LABEL, so this does
     too, for one event name per tab however it was reached. */
  const switchTab = (value: string) => {
    const label = items.find(item => item.value === value)?.label;
    if (label) {
      fireLinkClick({
        linkName: label,
        linkPosition: linkPosition || LINK_POSITION_BODY
      });
    }
    onSelect(value);
  };

  /* Only a <strong> naming a tab is swapped; everything else falls through to the
     default rendering, so the parse stays what Text renders apart from the matched
     runs. The inner <strong> is KEPT so the bold and underline come from
     `.dds-richText strong` / `.subheading strong` — a switch and a plain emphasis
     cannot drift apart, and the button adds only the interaction. */
  const options: HTMLReactParserOptions = {
    replace: node => {
      if (!isElement(node) || node.name !== "strong") {
        return undefined;
      }
      const value = valueByKey.get(matchKey(textOf(node)));
      if (value === undefined) {
        return undefined;
      }
      /* No className: `.subheading button` styles it, matching the element-scoped
         `.subheading strong` rule beside it. */
      return (
        <button
          aria-pressed={value === activeValue}
          onClick={() => switchTab(value)}
          type="button"
        >
          <strong>{domToReact(node.children as DOMNode[], options)}</strong>
        </button>
      );
    }
  };

  /* Mirrors the Text atom's own two decisions for an authored string, because this
     leaf hands Text pre-parsed NODES and Text only self-detects from a raw string:
     block markup forces a <div> host (a <p> would close at the first block child
     and break hydration), and `dds-richText` scopes the rich-text rules — notably
     capping <strong> at medium, which the subhead renders at. Without re-adding the
     class the non-matching bold runs would jump to the UA's 700. */
  const rich = hasMarkup(content);
  const host = rich && needsBlockHost(content) ? "div" : "p";
  const classes = rich ? `dds-richText ${styles.subheading}` : styles.subheading;

  /* subhead-2 base; `.subheading` pins the 24px size + the mobile/tablet→desktop cut, and
     paints the emphasis white. `tone="tertiary"` is the base colour; `weight="medium"`
     holds the mobile/tablet 500. WAT-12504 / design-conflicts. */
  return (
    <Text as={host} className={classes} tone="tertiary" variant="subhead-2" weight="medium">
      {parse(content, options)}
    </Text>
  );
}
export const HpTabCarouselSubheading = withSafeRender(HpTabCarouselSubheadingImpl, "HpTabCarouselSubheading");

export default HpTabCarouselSubheading;
